import { RestError } from '@azure/core-http';
import {
  ProjectDto,
  KnownSortOrder,
  KnownUserType,
  UserDto,
  EditUserDto,
  CreateUserDto,
  CreateCustomerDto,
  CreateOccupantDto,
  EditCustomerDto,
  CustomerDto,
  EditOccupantDto,
  KnownBatchUploadArchiveMissingOption,
  CustomersIntegrationControllerFileUploadResponse,
  OccupantsIntegrationControllerBatchUploadResponse,
  UsersControllerListOptionalParams,
  UsersNotesControllerFindAllOptionalParams
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { MutationFunction, useMutation, useQueryClient } from 'react-query';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';

export type UserStatus = 'active' | 'inactive';
export type UsersListQueryParams = Omit<UsersControllerListOptionalParams, 'pageNo'> & {
  // down cast sortOrder for type safety
  sortOrder?: KnownSortOrder;
};
export type UsersNotesListQueryParams = Omit<UsersNotesControllerFindAllOptionalParams, 'pageNo'> & {
  // down cast sortOrder for type safety
  sortOrder?: KnownSortOrder;
};

/** `status` is one value, not the endpoint's array: the select offers one and the URL carries one. */
export type UsersListControlsState = Required<
  Pick<UsersListQueryParams, 'sortBy' | 'sortOrder' | 'searchTerm' | 'managed'>
> & { status: string };

/** What callers pass: the query params, with `status` as the single value the controls hold. */
export type UsersListQueryInput = Omit<Partial<UsersListQueryParams>, 'status'> & { status?: string };

export type InfiniteQueryUsersListResultDto = {
  user: UserDto;
  project?: ProjectDto;
};

export function useInfiniteQueryUsersList(
  retailerID: string | undefined,
  projects: ProjectDto[],
  params: UsersListQueryInput,
  options?: { userTypes?: KnownUserType[]; queryKey?: string; includeChildProjects?: boolean }
) {
  const api = useApi();
  // Customers and occupants roll up from child projects, so that stays the default. Couriers do
  // not: a carrier project's list is its own couriers.
  const includeChildProjects = options?.includeChildProjects ?? true;

  const queryParams: UsersListQueryParams = {
    status: params.status && params.status !== 'all' ? [params.status] : undefined,
    sortBy: params.sortBy || undefined,
    sortOrder: params.sortOrder,
    searchTerm: params.searchTerm || undefined,
    managed: params.managed || undefined,
    userTypes: options?.userTypes,
    includeRemoved: params.includeRemoved || undefined,
    groupID: params.groupID || undefined,
    includeChildGroups: params.groupID ? true : undefined
  };

  return useInfiniteQuery(
    [options?.queryKey || QueryKeys.USERS, retailerID, projects, params, options?.userTypes, includeChildProjects],
    async ({ pageParam = 1 }) => {
      if (retailerID === undefined) {
        return {
          items: [],
          totalRecords: 0,
          perPageRecords: 25,
          totalPages: 1,
          pageNo: 1
        };
      }
      const result = await api.usersController.list({
        ...queryParams,
        projectID: retailerID,
        includeChildProjects,
        includeInheritedProjectModules: true,
        perPageRecords: 25,
        pageNo: pageParam
      });

      return {
        ...result,
        items: (result.items || []).map((user) => {
          return {
            user,
            project: projects.find((project) => project.id === user.projectId)
          };
        })
      };
    },
    {
      // Don't fetch customer data if there is no project data
      enabled: projects.length > 0,
      // FYI: when getNextPageParam returns undefined, the value of hasNextPage will be false
      getNextPageParam(lastPage) {
        if (lastPage.pageNo < lastPage.totalPages) {
          return lastPage.pageNo + 1;
        } else {
          return undefined;
        }
      }
    }
  );
}

/**
 * Searches occupants scoped to a set of projects, for the reserve-delivery-container flow.
 * The search term is expected to already be debounced by the caller.
 */
export function useQuerySearchOccupants({
  searchTerm,
  projectIDs,
  enabled = true
}: {
  searchTerm: string;
  projectIDs: string[];
  enabled?: boolean;
}) {
  const api = useApi();

  return useQuery<UserDto[], RestError>(
    [QueryKeys.USERS, QueryKeys.OCCUPANT_SEARCH, projectIDs, searchTerm],
    async () => {
      const result = await api.usersController.list({
        searchTerm: searchTerm || undefined,
        userTypes: [KnownUserType.Occupant],
        projectIDs,
        perPageRecords: 25,
        pageNo: 1
      });
      return result.items ?? [];
    },
    {
      enabled: enabled && projectIDs.length > 0,
      keepPreviousData: true
    }
  );
}

export function useInfiniteQueryUsersNotesList(userId: string, params: Partial<UsersNotesListQueryParams>) {
  const queryParams: UsersNotesListQueryParams = {
    sortBy: params.sortBy || undefined,
    sortOrder: params.sortOrder,
    includeNotesOnRelatedResources: params.includeNotesOnRelatedResources || true
  };

  const api = useApi();

  return useInfiniteQuery(
    [QueryKeys.USERS, userId, QueryKeys.NOTES, params],
    async ({ pageParam = 1 }) => {
      return await api.usersNotesController.findAll(userId, {
        ...queryParams,
        perPageRecords: 5,
        pageNo: pageParam
      });
    },
    {
      // Don't fetch customer data if there is no project data
      enabled: true,
      // FYI: when getNextPageParam returns undefined, the value of hasNextPage will be false
      getNextPageParam(lastPage) {
        if (lastPage.pageNo < lastPage.totalPages) {
          return lastPage.pageNo + 1;
        } else {
          return undefined;
        }
      }
    }
  );
}
export function useUserByIdQuery(
  projectId?: string,
  userId?: string,
  childProjectId?: string,
  options?: { onSuccess?(data: UserDto | undefined): void; onError?(error: unknown): void }
) {
  const api = useApi();
  const projectIdentifier = childProjectId || projectId;
  const result = useQuery<UserDto | undefined, RestError>(
    [QueryKeys.USERS, projectIdentifier, userId],
    async () => {
      if (projectIdentifier === undefined || userId === undefined) {
        return undefined;
      }
      return api.usersController.get(userId);
    },
    options
  );

  return result;
}

export function useMutationCreateCustomer() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CreateCustomerDto, RestError, { retailerID: string; createCustomerDto: CreateCustomerDto }>(
    async ({ createCustomerDto, retailerID }) => {
      return await api.customersController.customersCreate(retailerID, createCustomerDto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

export function useMutationCreateOccupant() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<UserDto, RestError, CreateOccupantDto>(
    async (createOccupantDto) => {
      return await api.occupantsController.create(createOccupantDto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

export function useMutationEditOccupant(userID?: string) {
  const api = useApi();

  const queryClient = useQueryClient();

  return useMutation<EditOccupantDto, RestError, EditOccupantDto>(
    async (editOccupantDto) => {
      if (!userID) {
        throw new Error('An unexpected error occurred: userID is not defined.');
      }
      return await api.occupantsController.edit(userID, editOccupantDto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

export function useMutationEditCustomer(userID?: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CustomerDto, RestError, { retailerID: string; editCustomerDto: EditCustomerDto }>(
    async ({ editCustomerDto, retailerID }) => {
      if (!userID) {
        throw new Error('An unexpected error occurred: userID is not defined.');
      }
      return await api.customersController.customersEdit(retailerID, userID, editCustomerDto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

export function useMutationCreateUser() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<UserDto, RestError, CreateUserDto>(
    async (createUserDto) => {
      return await api.usersController.create(createUserDto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

export function useMutationEditUser(userID?: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<UserDto, RestError, EditUserDto>(
    async (editUserDto) => {
      if (!userID) {
        throw new Error('An unexpected error occurred: userID is not defined.');
      }
      return await api.usersController.edit(userID, editUserDto);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}

export function useMutationBatchUploadCustomers() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    CustomersIntegrationControllerFileUploadResponse, // Mutation function response type
    RestError, // Error type
    {
      // Variables type
      projectID: string;
      archiveMissing: KnownBatchUploadArchiveMissingOption;
      csv: string;
    },
    MutationFunction<
      CustomersIntegrationControllerFileUploadResponse,
      { projectID: string; archiveMissing: KnownBatchUploadArchiveMissingOption; csv: string }
    > // Mutation function type
  >(
    async (variables) => {
      const { projectID, archiveMissing, csv } = variables;
      return await api.customersIntegrationController.fileUpload(projectID, archiveMissing, { csv } as any);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.CUSTOMERS]);
      }
    }
  );
}

export function useMutationBatchUploadOccupants() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    OccupantsIntegrationControllerBatchUploadResponse, // Mutation function response type
    RestError, // Error type
    {
      // Variables type
      projectID: string;
      archiveMissing: KnownBatchUploadArchiveMissingOption;
      csv: string;
    },
    MutationFunction<
      OccupantsIntegrationControllerBatchUploadResponse,
      { projectID: string; archiveMissing: KnownBatchUploadArchiveMissingOption; csv: string }
    > // Mutation function type
  >(
    async (variables) => {
      const { projectID, archiveMissing, csv } = variables;
      return await api.occupantsIntegrationController.fileUpload(projectID, archiveMissing, { csv });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.USERS]);
      }
    }
  );
}
